/**
 * Screen-space hit testing for the 2D map.
 *
 * Lifted out of `BuildingMapCanvas` because it is pure and its own test file
 * has always been named for a module that did not exist. The screen-space
 * floor is the whole point of this on a touch device, and there is no way to
 * observe it through the component: jsdom computes no layout, so a rendered
 * canvas has no size, no scale and no pointer geometry.
 */
import type { PlaceRegistry } from '../../api/v1';
import type { BuildingMap, DoorState, LiftState } from '../../api/ws-types';
import { CLEANING_ZONE_HIT_RADIUS_M } from './map-renderer';
import type { RobotMark } from './robot-marks';

export interface HitResult {
  kind: 'robot' | 'door' | 'lift' | 'place';
  id: string;
}

export function hitTest(
  wx: number,
  wy: number,
  /**
   * The robot marks on this level, EXACTLY the list the renderer drew
   * (`robotMarksForLevel`). A robot is hittable where its mark is and
   * nowhere else: a ghost at its last real pose, nothing for a robot
   * riding a lift or one that has lost its place, never at a stub's
   * or the origin sentinel's coordinates, and never a simulated robot
   * hidden from this audience.
   */
  robotMarks: readonly RobotMark[] | undefined,
  level: BuildingMap['levels'][number] | undefined,
  buildingMap: BuildingMap | undefined,
  liveDoorStates: Record<string, DoorState | undefined>,
  liveLiftStates: Record<string, LiftState | undefined>,
  activeLevelName: string,
  places: readonly PlaceRegistry[],
  /**
   * A FLOOR ON EVERY TARGET, IN WORLD UNITS, derived by the caller from
   * a screen distance and the current scale.
   *
   * Every radius below was a fixed world measurement -- 0.6 m for a
   * robot, 0.5 m for a door -- which is right once you are zoomed in
   * and useless when you are not. At fit-to-building on a phone the
   * map runs about 3.7 px per metre, so a robot's target was a circle
   * roughly TWO PIXELS across: not merely hard to hit by finger, hard
   * to hit with a mouse. The floor makes a target never smaller than
   * the pointer can reach, while the world baseline still wins once
   * the scale is high enough that it is the larger of the two -- so
   * zoomed in, nothing about the behaviour changes.
   *
   * IT DOES NOT REORDER ANYTHING. Each radius grows by the same screen
   * amount, so the deliberate precedence below (robot, then zone, then
   * door, then lift) is preserved; what changes is that overlapping
   * targets are more likely to overlap at low zoom, which the ordering
   * already exists to resolve.
   */
  minRadiusM = 0,
): HitResult | null {
  // Ordering is smallest-and-most-specific target first, so a click
  // that could plausibly mean two things resolves to the one the
  // operator was aiming at.
  //
  // Robots first (smallest markers, and the only moving ones). The
  // marks are already filtered to `activeLevelName` by the pose they
  // are drawn at.
  for (const m of robotMarks ?? []) {
    const dx = m.x - wx;
    const dy = m.y - wy;
    const rRobot = Math.max(0.6, minRadiusM);
    if (dx * dx + dy * dy <= rRobot * rRobot) {
      return { kind: 'robot', id: m.name };
    }
  }
  // Cleaning zones next — after robots (a robot parked on a zone is
  // the more urgent thing to open, and it is the smaller marker), but
  // BEFORE doors and lifts, which are coarser targets that would
  // otherwise swallow a zone marker sitting inside them. sg-nomad-01
  // declares `L04-Lift` as a cleaning zone, so a zone inside a lift's
  // bounding box is a real configuration, not a hypothetical; tested
  // last, that zone would be permanently unclickable.
  //
  // Zones ONLY: the ordinary waypoint dots are drawn as texture and
  // have no action behind them, so making them clickable would just
  // mean hundreds of targets per floor that do nothing. `places` is
  // already level-filtered by the caller (same list `drawPlaces` was
  // given), so a marker is clickable exactly where it is visible.
  for (const p of places) {
    if (!p.is_cleaning_zone) continue;
    const dx = p.x - wx;
    const dy = p.y - wy;
    const rZone = Math.max(CLEANING_ZONE_HIT_RADIUS_M, minRadiusM);
    if (dx * dx + dy * dy <= rZone * rZone) {
      return { kind: 'place', id: p.name };
    }
  }
  // Doors — pick the nearest line segment within tolerance.
  if (level) {
    let bestDoor: { name: string; dSq: number } | null = null;
    for (const d of level.doors ?? []) {
      const a = liveDoorStates[d.name] as { v1_x?: number; v1_y?: number; v2_x?: number; v2_y?: number } | undefined;
      const v1x = a?.v1_x ?? d.v1_x;
      const v1y = a?.v1_y ?? d.v1_y;
      const v2x = a?.v2_x ?? d.v2_x;
      const v2y = a?.v2_y ?? d.v2_y;
      const segDx = v2x - v1x;
      const segDy = v2y - v1y;
      const lenSq = segDx * segDx + segDy * segDy || 1;
      const t = Math.max(0, Math.min(1, ((wx - v1x) * segDx + (wy - v1y) * segDy) / lenSq));
      const projx = v1x + t * segDx;
      const projy = v1y + t * segDy;
      const ex = wx - projx;
      const ey = wy - projy;
      const dSq = ex * ex + ey * ey;
      const rDoor = Math.max(0.5, minRadiusM);
      if (dSq < rDoor * rDoor && (!bestDoor || dSq < bestDoor.dSq)) {
        bestDoor = { name: d.name, dSq };
      }
    }
    if (bestDoor) return { kind: 'door', id: bestDoor.name };
  }
  // Lifts.
  for (const lift of buildingMap?.lifts ?? []) {
    if (!lift.levels?.includes(activeLevelName)) continue;
    // A lift is a box rather than a disc, so the floor is added to each
    // half-extent instead of replacing a radius -- a 1m default lift at
    // fit scale was the same two-pixel target as a robot.
    const halfW = (lift.width ?? 1) / 2 + minRadiusM;
    const halfD = (lift.depth ?? 1) / 2 + minRadiusM;
    if (wx >= lift.ref_x - halfW && wx <= lift.ref_x + halfW && wy >= lift.ref_y - halfD && wy <= lift.ref_y + halfD) {
      return { kind: 'lift', id: lift.name };
    }
    void liveLiftStates;
  }
  return null;
}
